<template>
  <div class="home-page">
    <header class="site-header">
      <router-link class="brand" to="/">
        <span class="brand-mark" aria-hidden="true">
          <span></span>
        </span>
        <span>
          <strong>Codex API</strong>
          <em>codex API 中转服务</em>
        </span>
      </router-link>
      <nav class="site-nav" aria-label="top navigation">
        <el-button v-if="isLoggedIn" round plain @click="logout">退出登录</el-button>
        <el-button round type="primary" :loading="loading" @click="handleConsoleClick">
          {{ isLoggedIn ? '进入控制台' : '登录控制台' }}
        </el-button>
      </nav>
    </header>

    <main>
      <section class="hero-section">
        <div class="hero-copy">
          <h1>
            <span>Codex API</span>
            <span>中转服务</span>
          </h1>
          <p class="hero-lead">
            <span>让 Codex 工作流</span>
            <span>更稳定高效</span>
          </p>
          <p class="hero-desc">
            面向日常编码场景，提供统一接入、额度用量、账单与安全设置管理。
          </p>

          <div class="quick-cards">
            <article v-for="item in quickCards" :key="item.label" class="quick-card">
              <span>{{ item.label }}</span>
              <strong>{{ item.title }}</strong>
              <p>{{ item.text }}</p>
            </article>
          </div>

          <div class="hero-actions">
            <el-button round type="primary" size="medium" :loading="loading" @click="handleConsoleClick">
              {{ isLoggedIn ? '进入控制台' : '登录控制台' }}
            </el-button>
            <span class="connection-status">{{ statusText }}</span>
          </div>
        </div>

        <aside class="workflow-panel" aria-label="Codex API 工作流概览">
          <div class="panel-title">
            <span></span>
            <strong>Codex API 工作台</strong>
          </div>
          <div class="flow-line">
            <b>Codex</b>
            <i></i>
            <b>统一管理</b>
            <i></i>
            <b>工作台</b>
          </div>
          <div class="metric-grid">
            <article v-for="item in metrics" :key="item.label" class="metric-item">
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}</strong>
            </article>
          </div>
          <p class="panel-note">登录后可进入仪表盘，管理 API 密钥、额度、用量与账户信息。</p>
        </aside>
      </section>

      <section class="feature-section">
        <div class="section-heading">
          <h2>为什么选择 Codex API？</h2>
          <p>专为日常编码场景设计，帮助每一次调用都更稳定、清晰、可控。</p>
        </div>
        <div class="feature-grid">
          <article v-for="feature in features" :key="feature.title" class="feature-card">
            <span>{{ feature.index }}</span>
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.text }}</p>
          </article>
        </div>
      </section>
    </main>

    <footer class="site-footer">
      <div class="brand compact">
        <span class="brand-mark" aria-hidden="true">
          <span></span>
        </span>
        <span>
          <strong>Codex API</strong>
          <em>统一、清晰的服务管理入口。</em>
        </span>
      </div>
      <el-button round type="primary" :loading="loading" @click="loadHello">检测服务</el-button>
    </footer>
  </div>
</template>

<script>
import { fetchHello } from '../api/hello';

const LOGIN_STORAGE_KEY = 'codex-api-login-user';
const TOKEN_STORAGE_KEY = 'codex-api-auth-token';

export default {
  name: 'HomeView',
  data() {
    return {
      loading: false,
      isLoggedIn: false,
      statusText: '正在检测服务状态...',
      quickCards: [
        {
          label: '接入',
          title: '一键使用',
          text: '登录后可查看接入信息，并快速进入个人控制台。'
        },
        {
          label: '成本',
          title: '按需管理',
          text: '集中查看额度、用量与账单，便于控制团队使用。'
        },
        {
          label: '稳定',
          title: '长期可用',
          text: '通过统一控制台管理服务状态与安全设置。'
        }
      ],
      metrics: [
        { label: '接入状态', value: '清晰可见' },
        { label: '额度用量', value: '集中管理' },
        { label: '响应体验', value: '稳定优先' },
        { label: '数据分析', value: '按需追踪' }
      ],
      features: [
        {
          index: '01',
          title: '一键接入',
          text: '将密钥和接口信息集中管理，减少重复配置。'
        },
        {
          index: '02',
          title: '更低成本',
          text: '按需使用与按量管理，帮助团队更清晰地控制成本。'
        },
        {
          index: '03',
          title: '高稳定性',
          text: '通过统一服务管理与状态展示，为长时间编码会话提供支撑。'
        },
        {
          index: '04',
          title: '集中管理',
          text: '统一管理密钥、额度、用量与账单，减少分散维护成本。'
        },
        {
          index: '05',
          title: '低延迟体验',
          text: '关注响应速度和可用性，让编码过程中的等待更少。'
        },
        {
          index: '06',
          title: '详细数据分析',
          text: '按模型、接口、时间追踪用量，帮助掌握每一次调用。'
        }
      ]
    };
  },
  created() {
    this.restoreLogin();
    this.loadHello();
  },
  methods: {
    restoreLogin() {
      this.isLoggedIn = Boolean(
        window.localStorage.getItem(LOGIN_STORAGE_KEY) &&
        window.localStorage.getItem(TOKEN_STORAGE_KEY)
      );
    },
    handleConsoleClick() {
      this.$router.push(this.isLoggedIn ? '/dashboard' : '/login');
    },
    logout() {
      this.isLoggedIn = false;
      window.localStorage.removeItem(LOGIN_STORAGE_KEY);
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
      this.statusText = '已退出登录';
      this.$message.success('已退出登录');
    },
    async loadHello() {
      this.loading = true;
      this.statusText = '正在连接后端服务...';

      try {
        const { data } = await fetchHello();
        this.statusText = data && data.data ? data.data : '后端服务连接正常';
      } catch (error) {
        this.statusText = '后端服务暂不可用';
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
